<template>
  <div class="project-customer">
    <AccessBlock :id="PROJECT_CUSTOMER_MASTER_DATA_BLOCK">
      <div class="accordion mb-3" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-customer-master-data
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-id-card-o mr-2"></i>{{ $t('project_customer_master_data') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-customer-master-data" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectCustomerMasterData
                v-if="customerMasterDataProject"
                :blockId="PROJECT_CUSTOMER_MASTER_DATA_BLOCK"
                :project="customerMasterDataProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK">
      <div class="accordion mb-3" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-customer-installation-address
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-map-marker mr-2"></i>{{ $t('project_customer_installation_address') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-customer-installation-address" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectCustomerInstallationAddress
                v-if="customerInstallationAddressProject"
                :blockId="PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK"
                :project="customerInstallationAddressProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_CUSTOMER_TAX_BANK_BLOCK">
      <div class="accordion mb-3" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-customer-tax-bank
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-university mr-2"></i>{{ $t('project_customer_tax_bank') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-customer-tax-bank" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectCustomerTaxBank
                v-if="customerTaxBankProject"
                :blockId="PROJECT_CUSTOMER_TAX_BANK_BLOCK"
                :project="customerTaxBankProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK">
      <div class="accordion mb-3" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-customer-additional-information
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-info-circle mr-2"></i
            >{{ $t('project_customer_additional_information') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-customer-additional-information" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectCustomerAdditionalInformation
                v-if="customerAdditionalInformationProject"
                :blockId="PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK"
                :project="customerAdditionalInformationProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_PARTIES_BLOCK">
      <div class="accordion mt-3" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-customer-parties
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-users mr-2"></i>{{ $t('project_parties') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-customer-parties" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectParties
                v-if="partiesProject"
                :blockId="PROJECT_PARTIES_BLOCK"
                :project="partiesProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>
  </div>
</template>

<script>
import AccessBlock from '@/components/Access/AccessBlock.vue';
import ProjectCustomerMasterData from '@/blocks/project/ProjectCustomerMasterData.vue';
import ProjectCustomerInstallationAddress from '@/blocks/project/ProjectCustomerInstallationAddress.vue';
import ProjectCustomerTaxBank from '@/blocks/project/ProjectCustomerTaxBank.vue';
import ProjectCustomerAdditionalInformation from '@/blocks/project/ProjectCustomerAdditionalInformation.vue';
import ProjectParties from '@/blocks/project/ProjectParties.vue';
import {
  PROJECT_CUSTOMER_MASTER_DATA_BLOCK,
  PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK,
  PROJECT_CUSTOMER_TAX_BANK_BLOCK,
  PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK,
  PROJECT_PARTIES_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`); it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

export default {
  components: {
    AccessBlock,
    ProjectCustomerMasterData,
    ProjectCustomerInstallationAddress,
    ProjectCustomerTaxBank,
    ProjectCustomerAdditionalInformation,
    ProjectParties,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      PROJECT_CUSTOMER_MASTER_DATA_BLOCK,
      PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK,
      PROJECT_CUSTOMER_TAX_BANK_BLOCK,
      PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK,
      PROJECT_PARTIES_BLOCK,
    };
  },
  computed: {
    customerMasterDataProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.customerMasterData ?? null;
    },
    customerInstallationAddressProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.customerInstallationAddress ?? null;
    },
    customerTaxBankProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.customerTaxBank ?? null;
    },
    /**
     * The unsaved project goes to the block itself: notes and land register fields go with
     * `createProject`, the meter list shows its hint (plan
     * docs/work/_done/2026-10-project-block-customer-additional-information, decided 3).
     */
    customerAdditionalInformationProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.customerAdditionalInformation ?? null;
    },

    /**
     * Data of the block project-parties: the excerpt from `Project.blocks`, null when not loaded
     * or denied. The unsaved project has no blocks; the block gets the project itself only to
     * show its hint (plan docs/work/_done/2026-10-project-block-parties, decided 3).
     */
    partiesProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.parties ?? null;
    },
  },
};
</script>
